import { useCallback } from 'react';
import {
    ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL,
    ESRI_MAP_DEFAULT_ZOOM_LEVEL,
    ESRI_MAP_MAX_ZOOM_LEVEL,
    ESRI_MAP_MIN_ZOOM_LEVEL,
} from '../configurations';
import type { GeoPoint } from '../../location/types';
import type { UseZoomInAndOutControl } from './use-zoom-in-and-out-control';
import { useZoomInAndOutControl } from './use-zoom-in-and-out-control';

type UseMapControls = UseZoomInAndOutControl & {
    defaultCenter?: GeoPoint;
    entityViewZoomLevel?: number;
    defaultZoomLevel?: number;
    isFollowModeEnabled: boolean;
    setIsFollowModeEnabled: (isEnabled: boolean) => void;
    setAssignedUnitForFollowMode?: () => void;
};
export const useMapControls = ({
    mapView,
    defaultCenter,
    entityViewZoomLevel = ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL,
    defaultZoomLevel = ESRI_MAP_DEFAULT_ZOOM_LEVEL,
    maxZoomLevel = ESRI_MAP_MAX_ZOOM_LEVEL,
    minZoomLevel = ESRI_MAP_MIN_ZOOM_LEVEL,
    isFollowModeEnabled,
    setIsFollowModeEnabled,
    setAssignedUnitForFollowMode,
}: UseMapControls) => {
    const { onZoomIn, onZoomOut } = useZoomInAndOutControl({ mapView, maxZoomLevel, minZoomLevel });

    const setZoomLevel = useCallback(
        function setZoomLevel(zoomLevel = ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL): void {
            if (mapView) {
                mapView.zoom = zoomLevel;
            }
        },
        [mapView]
    );

    const resetMap = useCallback(
        function resetMap(): void {
            if (mapView && defaultCenter) {
                mapView
                    .goTo(
                        {
                            center: [defaultCenter.longitude, defaultCenter.latitude],
                        },
                        { animate: false }
                    )
                    .catch(() => {});
                setZoomLevel(defaultZoomLevel);
            }
        },
        [defaultCenter, mapView, defaultZoomLevel, setZoomLevel]
    );

    const focusEntity = useCallback(
        function focusEntity(): void {
            setAssignedUnitForFollowMode?.();
            if (isFollowModeEnabled) {
                setIsFollowModeEnabled(false);
            } else {
                setZoomLevel(entityViewZoomLevel);
                setIsFollowModeEnabled(true);
            }
        },
        [
            isFollowModeEnabled,
            setAssignedUnitForFollowMode,
            setIsFollowModeEnabled,
            setZoomLevel,
            entityViewZoomLevel,
        ]
    );
    return {
        zoomIn: onZoomIn,
        zoomOut: onZoomOut,
        resetMap,
        setZoomLevel,
        focusEntity,
    };
};
